export type LogoProps = {
  /** Rendered size in pixels, square. */
  size?: number;
  /**
   * An accessible name, when the mark stands alone.
   *
   * Left off wherever the product name is written beside it: the mark would
   * then be announced twice, and it is decoration next to real text.
   */
  label?: string;
  className?: string;
};

/**
 * The product mark: one request arriving, three destinations leaving.
 *
 * That is the whole of what this service does — take a request for one URL and
 * send it to one of several targets by weight — so the mark is the mechanism
 * rather than an abstract glyph. The single stroke on the left is the incoming
 * request; the three diverging arms are the split, each drawn in a colour from
 * the same categorical scale the split bar and the traffic chart use, so the
 * logo and the data are the same visual language.
 *
 * Drawn from tokens, never from literal colours (the design-system guard walks
 * every source file and rejects a hardcoded one), and it therefore restyles
 * itself in dark mode along with everything else.
 *
 * The arms thicken from top to bottom on purpose: an even fan reads as a
 * decoration, an uneven one reads as a weighted split.
 */
export function Logo({ size = 28, label, className }: LogoProps) {
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      fill="none"
      {...(label === undefined
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': label })}
    >
      {/* The incoming request. */}
      <path
        d="M3 16h8"
        stroke="var(--color-accent)"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <circle cx="4" cy="16" r="3" fill="var(--color-accent)" />

      {/* The split: three arms, three weights, three series colours. */}
      <path
        d="M11 16c6 0 6-10 13-10"
        stroke="var(--color-series-3)"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path
        d="M11 16h13"
        stroke="var(--color-series-2)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M11 16c6 0 6 10 13 10"
        stroke="var(--color-series-5)"
        strokeWidth="3.8"
        strokeLinecap="round"
      />

      {/* Where the traffic lands. */}
      <circle cx="26" cy="6" r="2.4" fill="var(--color-series-3)" />
      <circle cx="26" cy="16" r="2.8" fill="var(--color-series-2)" />
      <circle cx="26" cy="26" r="3.2" fill="var(--color-series-5)" />
    </svg>
  );
}
